import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { useConditionalFreeze } from '@/domain/hooks/useConditionalFreeze'
import { InjectedActionsContext, Objective } from '@/features/actions/logic/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { UseFormReturn, useForm, useWatch } from 'react-hook-form'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import BigNumber from 'bignumber.js'
import { calculateIsolatedHealthFactor, calculateUpdatedHealthFactor } from '@/domain/common/calculations/healthFactor'

import {
  AssetInputSchema,
  DialogFormNormalizedData,
  UseDebouncedDialogFormValuesResult,
} from '@/features/dialogs/common/logic/form'
import { useDebounce } from '@/utils/useDebounce'
import { MyWalletProps } from '@/features/isolated-details/components/my-wallet/MyWallet'
import { IsolatedPairOverview } from '@/features/isolated-details/types'
import { getRepayDialogFormValidator, getFormFieldsForRepayDialog } from './form'
import { useReadContract } from 'wagmi'
import { FRAXLEND_PAIR_ABI } from '@/config/abis/FraxlendPair.abi'
import { PositionOverview } from './types'
import { calculateLiquidationPrice } from '@/domain/common/calculations/liquidation'
import { track } from '@/features/analytics'

export interface UseDebouncedDialogFormValuesArgs {
  form: UseFormReturn<AssetInputSchema>
  walletOverview: MyWalletProps
}

export function useDebouncedDialogFormValues({
  form,
  walletOverview,
}: UseDebouncedDialogFormValuesArgs): UseDebouncedDialogFormValuesResult {
  const formValues = useWatch({ control: form.control })
  const isFormValid = form.formState.isValid

  const [keyCounter, setKeyCounter] = useState(0)
  const prevFormValuesRef = useRef(formValues)
  const prevIsFormValidRef = useRef(isFormValid)

  const normalizedFormValues = {
    token: walletOverview.repay?.token,
    value: NormalizedUnitNumber(formValues?.value || '0'),
    isMaxSelected: formValues?.isMaxSelected || false,
    position: {
      collateralBalance: NormalizedUnitNumber(0),
    },
    reserve: {
      supplyAPY: Percentage(0),
    },
  } as DialogFormNormalizedData

  useEffect(() => {
    const prevValues = prevFormValuesRef.current
    const prevIsValid = prevIsFormValidRef.current

    // Check for changes in important properties
    const hasChanged =
      prevValues?.symbol !== formValues?.symbol ||
      prevValues?.value !== formValues?.value ||
      prevValues?.isMaxSelected !== formValues?.isMaxSelected ||
      prevIsValid !== isFormValid

    if (hasChanged) {
      prevFormValuesRef.current = formValues
      prevIsFormValidRef.current = isFormValid
      setKeyCounter((prev) => prev + 1)
    }
  }, [formValues, isFormValid])

  const key = `form-change-${keyCounter}`

  const { debouncedValue, isDebouncing } = useDebounce({ formValues: normalizedFormValues, isFormValid }, key)

  return {
    debouncedFormValues: debouncedValue.formValues,
    isFormValid: debouncedValue.isFormValid,
    isDebouncing,
  }
}

export interface UseRepayDialogResult {
  repayOptions: TokenWithBalance[]
  assetsToRepayFields: FormFieldsForDialog
  repayAsset: TokenWithValue
  objectives: Objective[]
  actionsContext: InjectedActionsContext
  pageStatus: PageStatus
  form: UseFormReturn<AssetInputSchema>
  sharesToRepay: bigint
  currentPositionOverview: PositionOverview
  updatedPositionOverview?: PositionOverview
  updatedLiquidationPrice: NormalizedUnitNumber
}

export function useRepayDialog({
  walletOverview,
  pairOverview,
}: { walletOverview: MyWalletProps; pairOverview: IsolatedPairOverview }): UseRepayDialogResult {
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const [sharesToRepay, setSharestoRepay] = useState<bigint>(0n)

  // Get current borrowed amount
  const borrowedAmount = walletOverview.repay?.available || NormalizedUnitNumber(0)
  const walletBalance = walletOverview.depositBorrowAsset?.available || NormalizedUnitNumber(0)

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(
      getRepayDialogFormValidator({
        walletOverview,
      }),
      undefined,
      { raw: true },
    ),
    defaultValues: {
      symbol: walletOverview.repay?.token.symbol,
      value: '',
    },
    mode: 'onChange',
  })

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedDialogFormValues({
    form,
    walletOverview,
  })
  const lastTrackedAmountKeyRef = useRef('')

  useEffect(() => {
    if (!formValues.token || formValues.value.eq(0)) {
      return
    }

    const key = `${formValues.token.address}-${formValues.value.toFixed()}`
    if (key === lastTrackedAmountKeyRef.current) {
      return
    }

    lastTrackedAmountKeyRef.current = key

    track('lend_borrow_amount_entered', {
      token: formValues.token.symbol,
      market: formValues.token.address,
      market_type: 'isolated',
      action_type: 'repay',
      amount: formValues.value.toFixed(),
    })
  }, [formValues])

  const repayAsset = useConditionalFreeze(formValues, pageStatus === 'success')

  const repayAmountInBaseUnits = repayAsset.value.gt(0)
    ? BigInt(Math.floor(repayAsset.value.toNumber() * 10 ** walletOverview.repay!.token.decimals))
    : 0n

  // Calculate shares using toBorrowShares with correct parameters
  const { data: calculatedShares, isLoading: isCalculatingShares } = useReadContract({
    address: walletOverview.pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'toBorrowShares',
    args: [
      repayAmountInBaseUnits,
      true, // roundUp for repaying (to ensure sufficient shares)
      false, // previewInterest
    ],
    query: {
      enabled: repayAsset.value.gt(0) && isFormValid,
    },
  })

  const { data: fullAmount, isLoading: isCalculatingFullAmount } = useReadContract({
    address: walletOverview.pairAddress,
    abi: FRAXLEND_PAIR_ABI,
    functionName: 'toBorrowAmount',
    args: [
      calculatedShares ?? 0n,
      true, // roundUp for repaying (to ensure sufficient shares)
      true, // previewInterest
    ],
    query: {
      enabled: repayAsset.value.gt(0) && isFormValid && !!calculatedShares,
    },
  })

  const bufferedFullAmount = new BigNumber((fullAmount ?? 0n).toString())
    .multipliedBy(1.00001)
    .integerValue(BigNumber.ROUND_FLOOR)

  // Store calculated shares
  useEffect(() => {
    if (calculatedShares) {
      setSharestoRepay(calculatedShares as bigint)
    } else {
      setSharestoRepay(0n)
    }
  }, [calculatedShares])

  const assetsToRepayFields = getFormFieldsForRepayDialog(
    form,
    walletOverview.repay!.token,
    walletOverview.repay!.available,
    walletOverview.depositBorrowAsset!.available,
  )

  const maxLtv = pairOverview.maxLtv
  const normalizedExchangeRate = pairOverview.normalizedExchangeRate
  const collateralAmount = walletOverview.withdraw?.total || NormalizedUnitNumber(0)

  const currentHealthFactor = calculateIsolatedHealthFactor(
    collateralAmount,
    normalizedExchangeRate,
    borrowedAmount,
    maxLtv,
  )

  const updatedHealthFactor = calculateUpdatedHealthFactor({
    collateralAmount,
    collateralExchangeRate: normalizedExchangeRate,
    borrowedAmount,
    maxLtv,
    borrowChange: repayAsset.value.toNumber(),
    isBorrowIncrease: false,
    isIsolated: true,
  })

  const collateralValueUSD = NormalizedUnitNumber(
    NormalizedUnitNumber(collateralAmount).multipliedBy(walletOverview.liquidationDetails.tokenWithPrice.priceInUSD),
  )

  const borrowValueUSD = NormalizedUnitNumber(borrowedAmount.minus(repayAsset.value)).multipliedBy(
    walletOverview.borrow?.token.unitPriceUsd,
  )

  const currentLtv =
    collateralValueUSD.isZero() || borrowValueUSD.isZero()
      ? Percentage(0)
      : Percentage(borrowValueUSD.dividedBy(collateralValueUSD), true)

  const updatedLiquidationPrice = calculateLiquidationPrice(
    walletOverview.liquidationDetails.tokenWithPrice.priceInUSD,
    currentLtv,
    pairOverview.maxLtv,
  )

  const currentPositionOverview: PositionOverview = {
    healthFactor: currentHealthFactor,
    tokenSupply: collateralAmount,
    supplyAPY: pairOverview.lendAPY,
  }

  // Updated position overview after repay
  const updatedPositionOverview = repayAsset.value.eq(0)
    ? undefined
    : ({
        ...currentPositionOverview,
        healthFactor: updatedHealthFactor,
      } as PositionOverview)

  // Only enable actions when all calculations are complete
  const actionsEnabled =
    repayAsset.value.gt(0) &&
    isFormValid &&
    !isDebouncing &&
    !isCalculatingShares &&
    !isCalculatingFullAmount &&
    pageStatus !== 'success'

  // Add a small buffer to the approval amount to account for precision issues
  const bufferedApprovalValue = repayAsset.value.gt(0)
    ? NormalizedUnitNumber(new BigNumber(repayAsset.value.toNumber()).multipliedBy(1.001))
    : repayAsset.value

  return {
    form,
    repayOptions: [{ token: walletOverview.repay!.token, balance: borrowedAmount }],
    assetsToRepayFields,
    repayAsset,
    objectives: [
      {
        type: 'isolatedRepay',
        token: walletOverview.repay!.token,
        value: repayAsset.value,
        pairAddress: walletOverview.pairAddress,
        shares: sharesToRepay.toString(),
        approvalValue: bufferedApprovalValue,
        repayFull:
          bufferedApprovalValue.gt(borrowedAmount) &&
          !!walletOverview.depositBorrowAsset?.token &&
          walletBalance.gt(0) &&
          bufferedFullAmount.gt(0) &&
          walletOverview.depositBorrowAsset.token.toBaseUnit(walletBalance).gt(bufferedFullAmount),
      },
    ],
    actionsContext: {
      isolatedInfo: {
        pairAddress: walletOverview.pairAddress,
      },
    },
    pageStatus: {
      actionsEnabled,
      state: pageStatus,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    sharesToRepay,
    currentPositionOverview,
    updatedPositionOverview,
    updatedLiquidationPrice,
  }
}
